import { useCallback, useEffect, useState } from 'react';

import { useAppSelector } from '~/hooks';
import { userAuthSelector } from '~/state/selectors';
import LocalStorage from '~/state/localStorage';

// Bump when the declaration is materially rewritten, to ask everyone again.
export const STRATEGY_CONSENT_VERSION = 1;

const CONSENT_LABEL = 'ai-strategy-enable-consent';

/**
 * The terms-of-business declaration a user makes before any strategy goes live, asked once per
 * person rather than once per strategy.
 *
 * Account-scoped, unlike LocalStorage.onboarding: the declaration belongs to a person, so a second
 * user logging in on the same device makes their own. It survives logout so returning users are
 * not asked again.
 *
 * `consentLabel` is undefined once consent is on file — ConfirmDialog then omits the checkbox and
 * its label entirely. It stays defined until the stored record has actually been read, so a slow
 * or failed read can never let an un-consented user straight through to Confirm.
 */
export const useStrategyConsent = () => {
    const { data } = useAppSelector(userAuthSelector);
    const accountKey = (data?.email ?? '').trim().toLowerCase();
    const [consented, setConsented] = useState(false);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        let active = true;
        setLoaded(false);
        setConsented(false);
        LocalStorage.strategyConsent
            .get()
            .then((record) => {
                if (!active) {
                    return;
                }
                setConsented(Boolean(accountKey) && (record?.[accountKey] ?? 0) >= STRATEGY_CONSENT_VERSION);
                setLoaded(true);
            })
            .catch(() => {
                // Storage unavailable: ask again rather than assume a declaration was made.
                if (active) {
                    setLoaded(true);
                }
            });
        return () => {
            active = false;
        };
    }, [accountKey]);

    // Read-modify-write of the whole map, matching useOnboarding: a person confirms one dialog at
    // a time, so there is no concurrent writer to lose. Never throws — failing to persist means
    // being asked once more, which must not block the action the user just confirmed.
    const recordConsent = useCallback(async () => {
        if (!accountKey) {
            return;
        }
        setConsented(true);
        try {
            const stored = (await LocalStorage.strategyConsent.get()) ?? {};
            await LocalStorage.strategyConsent.set({ ...stored, [accountKey]: STRATEGY_CONSENT_VERSION });
        } catch (e) {
            // Keep the in-memory flag: no repeat prompt this session, asked again on the next launch.
        }
    }, [accountKey]);

    return { consentLabel: loaded && consented ? undefined : CONSENT_LABEL, recordConsent };
};
